/* ==========================================================================
   ORGANIC NETRA — main.js

   Shell behaviour: drawers, cart, predictive search, wishlist, helpers.
   Loads before sections.js and exposes shared helpers on window.ONTheme.

   The design is untouched. Liquid renders the markup; this file enhances
   it and talks to Shopify's Cart AJAX API, so inventory, pricing and
   discounts stay authoritative on the server.
   ========================================================================== */
window.ONTheme = (function () {
  "use strict";

  /* ----------------------------------------------------------- helpers -- */
  var qs = function (s, r) { return (r || document).querySelector(s); };
  var qsa = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var routes = window.routes || {};
  var strings = window.cartStrings || {};

  function lockScroll(on) { document.body.style.overflow = on ? "hidden" : ""; }

  function toast(message) {
    var host = qs(".toasts");
    if (!host) return;
    var node = document.createElement("div");
    node.className = "toast";
    node.setAttribute("role", "status");
    node.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 12.5l5 5 10-11"/></svg><span></span>';
    node.querySelector("span").textContent = message;
    host.appendChild(node);
    setTimeout(function () {
      node.style.transition = "opacity .35s";
      node.style.opacity = "0";
      setTimeout(function () { node.remove(); }, 380);
    }, 2600);
  }

  function trapFocus(container, onEscape) {
    function onKey(e) {
      if (e.key === "Escape") { onEscape(); return; }
      if (e.key !== "Tab") return;
      var f = qsa('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
        .filter(function (n) { return n.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener("keydown", onKey);
    return function () { container.removeEventListener("keydown", onKey); };
  }

  /* Storage is only used for the wishlist, which is a browser-side
     convenience. Cart state always lives in Shopify. */
  var memory = {};
  var storage = {
    get: function (k, d) {
      try { var raw = localStorage.getItem(k); if (raw != null) return JSON.parse(raw); } catch (e) {}
      return k in memory ? memory[k] : d;
    },
    set: function (k, v) {
      memory[k] = v;
      try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {}
    },
  };

  /* ------------------------------------------------------ scroll reveal -- */
  function observeReveals(root) {
    var nodes = qsa(".reveal:not(.is-in)", root || document);
    if (!("IntersectionObserver" in window)) {
      nodes.forEach(function (n) { n.classList.add("is-in"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.06 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ============================================================ DRAWERS === */
  function drawer(el, openers, closerSelector) {
    if (!el) return null;
    var release;
    function open() {
      el.classList.add("is-open");
      lockScroll(true);
      release = trapFocus(el, close);
      setTimeout(function () {
        var f = qs("input, button, a", el);
        if (f) f.focus();
      }, 240);
    }
    function close() {
      el.classList.remove("is-open");
      lockScroll(false);
      if (release) release();
    }
    qsa(openers).forEach(function (b) { b.addEventListener("click", open); });
    qsa(closerSelector, el).forEach(function (b) { b.addEventListener("click", close); });
    return { open: open, close: close, el: el };
  }

  var cartDrawer, searchOverlay, mobileNav;

  function initDrawers() {
    mobileNav = drawer(qs("[data-mobile-nav]"), "[data-open-nav]", "[data-close-nav]");
    searchOverlay = drawer(qs("[data-search]"), "[data-open-search]", "[data-close-search]");
    cartDrawer = drawer(qs("[data-cart-drawer]"), "[data-open-cart]", "[data-close-drawer]");

    document.addEventListener("keydown", function (e) {
      if (e.key === "/" && document.activeElement === document.body) {
        e.preventDefault();
        if (searchOverlay) searchOverlay.open();
      }
    });

    var header = qs("[data-header]");
    if (header) {
      var onScroll = function () { header.classList.toggle("is-stuck", window.scrollY > 8); };
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    }
  }

  /* =============================================================== CART === */
  function updateCartCount(count) {
    qsa(".cart-count").forEach(function (n) {
      n.textContent = count;
      n.hidden = count === 0;
    });
  }

  /* Pull a freshly rendered cart drawer from Shopify and swap it in, so
     line prices, discounts and totals always come from the server. */
  function refreshCartDrawer() {
    return fetch(routes.cart_url + "?section_id=cart-drawer-refresh")
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var fresh = parsed.querySelector("[data-cart-drawer]");
        var live = qs("[data-cart-drawer]");
        if (!fresh || !live) return;
        var wasOpen = live.classList.contains("is-open");
        live.innerHTML = fresh.innerHTML;
        if (wasOpen) live.classList.add("is-open");
        bindCartDrawer();
      })
      .catch(function () {});
  }

  function bindCartDrawer() {
    var live = qs("[data-cart-drawer]");
    if (!live) return;
    qsa("[data-close-drawer]", live).forEach(function (b) {
      b.addEventListener("click", function () {
        live.classList.remove("is-open");
        lockScroll(false);
      });
    });
  }

  function addToCart(variantId, quantity, button) {
    if (!variantId) return;
    var label = button ? button.querySelector("[data-add-label]") || button : null;
    var original = label ? label.textContent : null;
    if (button) { button.disabled = true; }
    if (label) { label.textContent = "\u2026"; }

    return fetch(routes.cart_add_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] }),
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) {
          /* Shopify rejects the add when stock ran out mid-session. */
          toast(res.data.description || res.data.message || strings.error);
          return;
        }
        return fetch(routes.cart_url + ".js")
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            return refreshCartDrawer().then(function () {
              var live = qs("[data-cart-drawer]");
              if (live) {
                live.classList.add("is-open");
                lockScroll(true);
              }
              toast(strings.added);
            });
          });
      })
      .catch(function () { toast(strings.error); })
      .finally(function () {
        if (button) button.disabled = false;
        if (label && original) label.textContent = original;
      });
  }

  function changeLine(line, quantity) {
    return fetch(routes.cart_change_url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        if (document.body.dataset.page === "cart") {
          window.location.reload();
          return;
        }
        return refreshCartDrawer();
      })
      .catch(function () { toast(strings.error); });
  }

  function initCart() {
    bindCartDrawer();

    document.addEventListener("click", function (e) {
      var add = e.target.closest("[data-add-to-cart]");
      if (add) {
        e.preventDefault();
        /* An inline size dropdown, if the card has one, wins. */
        var card = add.closest(".card, .launch__item, .reel");
        var select = card ? card.querySelector("[data-inline-variant]") : null;
        var id = select ? select.value : add.dataset.variantId;
        addToCart(id, 1, add);
        return;
      }

      var qty = e.target.closest("[data-qty-change]");
      if (qty) {
        e.preventDefault();
        changeLine(qty.dataset.line, qty.dataset.qty);
      }
    });

    /* Product form: intercept so the drawer opens instead of a page jump. */
    var form = qs("[data-product-form]");
    if (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var input = form.querySelector("[data-variant-input]");
        var button = form.querySelector("[data-add-button]");
        addToCart(input.value, 1, button);
      });
    }
  }

  /* ===================================================== PREDICTIVE SEARCH */
  function initSearch() {
    var input = qs("[data-search-input]");
    var results = qs("[data-search-results]");
    if (!input || !results) return;
    var timer;

    input.addEventListener("input", function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ""; return; }
      timer = setTimeout(function () {
        var url = routes.predictive_search_url + "?q=" + encodeURIComponent(q) +
          "&resources[type]=product&resources[limit]=6&section_id=predictive-search";
        fetch(url)
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (html) {
            if (!html) return;
            var parsed = new DOMParser().parseFromString(html, "text/html");
            var fresh = parsed.querySelector("[data-predictive-results]");
            results.innerHTML = fresh ? fresh.innerHTML : "";
          })
          .catch(function () {});
      }, 220);
    });
  }

  /* ============================================================ WISHLIST ==
     Browser-side only. Swap for a wishlist app if you want it to persist
     across devices and feed into marketing. */
  var WISH_KEY = "on.wishlist.v1";
  function initWishlist() {
    var list = storage.get(WISH_KEY, []);

    function paint() {
      qsa("[data-wishlist]").forEach(function (b) {
        var on = list.indexOf(b.dataset.wishlist) > -1;
        b.setAttribute("aria-pressed", on ? "true" : "false");
        if (b.classList.contains("btn")) b.textContent = on ? "Saved" : "Save for later";
      });
    }

    document.addEventListener("click", function (e) {
      var b = e.target.closest("[data-wishlist]");
      if (!b) return;
      e.preventDefault();
      var id = b.dataset.wishlist;
      var i = list.indexOf(id);
      if (i > -1) { list.splice(i, 1); toast(strings.removed); }
      else { list.push(id); toast(strings.saved); }
      storage.set(WISH_KEY, list);
      paint();
    });

    paint();
  }

  /* ================================================================ BOOT == */
  function init() {
    initDrawers();
    initCart();
    initSearch();
    initWishlist();
    observeReveals();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  document.addEventListener("shopify:section:load", init);

  /* Shared with sections.js */
  return {
    qs: qs,
    qsa: qsa,
    toast: toast,
    trapFocus: trapFocus,
    lockScroll: lockScroll,
    storage: storage,
    observeReveals: observeReveals,
    routes: routes,
    strings: strings,
    addToCart: addToCart,
    changeLine: changeLine,
    openCart: function () {
      var live = qs("[data-cart-drawer]");
      if (live) { live.classList.add("is-open"); lockScroll(true); }
    }
  };
})();
